<template>
  <div v-if="campaign" class="pt-4 w-100 pr-8">
    <div class="flex flex-col gap-6">
      <div class="flex">
        <div
          class="row-label"
          @click="$refs.approvalRequiredTypesEditable.focus()"
        >
          Approval Required
        </div>
        <div class="row-content">
          <div
            v-if="campaign.payoutType == 'cpa' || campaign.payoutType == 'cpl'"
          >
            <EditableValue
              ref="approvalRequiredTypesEditable"
              :value="campaign.approvalRequiredTypes"
              type="slot"
              :mutation="UPDATE_CAMPAIGN_MUTATION"
              :variables="{
                input: {
                  id: campaign.id,
                  approvalRequiredTypes: '{value}'
                }
              }"
            >
              <template v-slot:default="slotProps">
                <div v-if="slotProps.value && slotProps.value.length > 0">
                  <Tag
                    v-for="tag in slotProps.value"
                    :key="tag"
                    class="mr-1 my-1"
                    :color="tag"
                    tabindex="0"
                    >{{ tag }}</Tag
                  >
                </div>
                <div v-else>
                  <span class="italic text-gray-500">None</span>
                </div>
              </template>
            </EditableValue>
          </div>

          <div v-else>
            <EditableValue
              ref="approvalRequiredTypesEditable"
              :value="campaign.approvalRequiredTypes"
              type="slot"
              :mutation="UPDATE_CAMPAIGN_MUTATION"
              :variables="{
                input: {
                  id: campaign.id,
                  approvalRequiredTypes: '{value}'
                }
              }"
            >
              <template v-slot:default="slotProps">
                <div v-if="slotProps.value && slotProps.value.length > 0">
                  <Tag
                    v-for="tag in slotProps.value"
                    :key="tag"
                    class="mr-1 my-1"
                    :color="tag"
                    tabindex="0"
                    @focus="
                      () => {
                        slotProps.focus()
                      }
                    "
                    >{{ tag }}</Tag
                  >
                </div>
                <div
                  v-else
                  @click="
                    () => {
                      slotProps.focus()
                    }
                  "
                >
                  <span
                    class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                    >None</span
                  >
                </div>
              </template>
              <template v-slot:focusSlot="slotProps">
                <Superselect
                  title="Approval Required"
                  :value="slotProps.value"
                  @input="
                    value => {
                      slotProps.updateInternalValue(value)
                    }
                  "
                  @close="
                    () => {
                      //slotProps.unfocusOnNoChanges() || slotProps.focusOnSave()
                    }
                  "
                  :focusOnMount="true"
                  :multiple="true"
                  placeholder="Select"
                  style="min-width: 10rem"
                  :options="[
                    'all',
                    'quarantined',
                    'quality',
                    'fraud',
                    'compliance'
                  ]"
                ></Superselect>
              </template>
            </EditableValue>
          </div>
        </div>
      </div>

      <div v-if="campaign.payoutType == 'cpa'" class="flex">
        <div class="row-label">
          Baseline Minimums
        </div>
        <div class="row-content">
          <div class="flex flex-col gap-2">
            <div>
              <Superselect
                title="Geos"
                :multiple="false"
                v-model="baselineGeo"
                :options="baselineCountries"
                placeholder="Select"
              />
            </div>
            <div>
              <YInput v-model="baselineValue" label="Baseline" />
            </div>
            <div class="flex gap-2">
              <YInput v-model="baselineMinimum" label="Minimum Deposit" />
              <YButton
                @click="addNewBaselineRow"
                class="bg-inverse-700 text-white h-8 w-8 rounded flex justify-center items-center"
                :disabled="!baselineGeo || !baselineValue || !baselineMinimum"
              >
                <Icon name="plus" size="4" />
              </YButton>
            </div>
          </div>

          <YTable
            v-if="baselineDeposits.length > 0"
            :data="baselineDeposits"
            class="m-0 p-0"
          >
            <template slot="header-row">
              <th>Geo</th>
              <th>Baseline</th>
              <th>Min</th>
              <th></th>
            </template>
            <template slot="row" slot-scope="props">
              <td>
                {{ props.rowData.geo }}
              </td>
              <td>{{ props.rowData.baseline }}</td>
              <td>{{ props.rowData.minimum }}</td>

              <td style="width:20px;">
                <div @click="deleteRow(props.rowData)">
                  <Icon
                    name="trashCan"
                    :size="4"
                    class="text-red-600"
                    v-tooltip="'Delete Row'"
                  />
                </div>
              </td>
            </template>
          </YTable>
        </div>
      </div>

      <div class="flex">
        <div class="row-label">
          Show Front-end
        </div>
        <div class="row-content">
          <Tag
            :color="
              campaign.showFrontend === 'yTrack'
                ? 'blue'
                : campaign.showFrontend === 'All In Affiliates'
                ? 'indigo'
                : 'gray'
            "
            class="mr-1 mb-1"
          >
            {{ campaign.showFrontend }}
          </Tag>
        </div>
      </div>

      <div class="flex">
        <div
          class="row-label"
          @click="
            $router.push({
              name: 'Campaign',
              params: { id: id, tab: 'offers' }
            })
          "
        >
          Advertisers
        </div>
        <div class="row-content">
          <div class="box-width">
            <div
              class="overflow-y-scroll rounded cursor-pointer hover:shadow-md focus:shadow-md outline-none"
              style="max-height: 8rem"
            >
              <router-link
                v-for="user of advertisers"
                :to="{
                  name: 'User',
                  params: { id: user.id }
                }"
                class="link truncate block hover:underline"
                :key="user.id"
              >
                <Tag
                  color="blue"
                  :shade="100"
                  class="block py-2 rounded-none"
                  :hover="true"
                  >{{ user.label }}</Tag
                >
              </router-link>
            </div>
            <div class="flex justify-between">
              <div></div>
              <div>
                <small
                  v-if="campaign.offers"
                  @click.prevent="
                    $router.push({
                      name: 'Campaign',
                      params: { id: id, tab: 'offers' }
                    })
                  "
                  class="font-semibold text-gray-700 text-blue-600 hover:text-blue-700 cursor-pointer"
                >
                  {{ advertisers.length }} Advertiser<template
                    v-if="advertisers.length > 1"
                    >s</template
                  >
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex" v-if="!isSalesAdmin">
        <div class="row-label" @click="openPermissionsModal">Access Rules</div>
        <div class="row-content">
          <template v-if="campaign && !campaign.showFrontend">
            <div class="text-xs font-semibold uppercase italic text-gray-500">
              Show Front-end selection required
            </div>
          </template>
          <template v-if="campaign.publisherAbilitySummary">
            <div class="box-width">
              <div
                class="overflow-y-scroll rounded cursor-pointer hover:shadow-md focus:shadow-md outline-none"
                style="max-height: 8rem"
                @click="openPermissionsModal"
                @keydown.enter="openPermissionsModal"
                tabindex="0"
              >
                <Tag
                  v-if="campaign.publisherAbilitySummary.all"
                  color="gray"
                  :shade="100"
                  class="block py-2 rounded-none"
                  >All Publishers</Tag
                >
                <Tag
                  v-for="only of campaign.publisherAbilitySummary.only"
                  color="blue"
                  :shade="100"
                  :key="only.id"
                  class="block py-2 rounded-none"
                  >{{ only.label }}</Tag
                >

                <Tag
                  v-for="except of campaign.publisherAbilitySummary.except"
                  color="blue"
                  :shade="100"
                  :key="except.id"
                  class="block py-2 rounded-none bg-red-100 text-red-600"
                  >{{ except.label }}</Tag
                >
              </div>
              <div class="text-right">
                <small
                  v-if="campaign.publisherAbilitySummary.except.length"
                  class="font-semibold text-red-600"
                >
                  {{ campaign.publisherAbilitySummary.except.length }}
                  Excluded
                </small>
                <small
                  v-if="campaign.publisherAbilitySummary.only.length"
                  class="font-semibold text-blue-600"
                  >{{
                    campaign.publisherAbilitySummary.only.length
                  }}
                  Only</small
                >
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="flex" v-if="!isSalesAdmin">
        <div class="row-label">Subscribers</div>
        <div class="row-content">
          <template v-if="campaign && !campaign.showFrontend">
            <div class="text-xs font-semibold uppercase italic text-gray-500">
              Show Front-end selection required
            </div>
          </template>
          <template v-if="campaign && campaign.subscribers">
            <div class="box-width">
              <div
                class="overflow-y-scroll rounded cursor-pointer hover:shadow-md focus:shadow-md outline-none"
                style="max-height: 8rem"
              >
                <router-link
                  v-for="user of campaign.subscribers"
                  :to="{
                    name: 'User',
                    params: { id: user.id }
                  }"
                  class="link truncate block hover:underline"
                  :key="user.id"
                >
                  <Tag
                    color="blue"
                    :shade="100"
                    class="block py-2 rounded-none"
                    :hover="true"
                    >{{ user.label }}</Tag
                  >
                </router-link>
              </div>
              <div class="flex justify-between">
                <div>
                  <small>
                    <a
                      href="#"
                      class="link cursor-pointer mt-1 block font-semibold"
                      @click.prevent="openSubscribeUserModal"
                      tabindex="0"
                      >Add Subscribers</a
                    >
                  </small>
                </div>
                <div>
                  <small
                    v-if="campaign.subscribers"
                    class="font-semibold text-gray-700 text-blue-600 hover:text-blue-500 cursor-pointer"
                    @click.prevent="openSubscribeUserModal"
                  >
                    {{ campaign.subscribers.length }} Subscriber<template
                      v-if="campaign.subscribers.length > 1"
                      >s</template
                    >
                  </small>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="flex mt-4">
        <div class="row-label">
          <div class="flex gap-3">
            <div
              class="row-label-text"
              @click="$refs.smartLinkEditable.toggleCheckbox()"
            >
              Smart Link
            </div>
            <div class="row-content">
              <EditableValue
                ref="smartLinkEditable"
                :value="campaign.smartLink"
                :mutation="UPDATE_CAMPAIGN_MUTATION"
                type="checkbox"
                :variables="{
                  input: {
                    id: campaign.id,
                    smartLink: '{value}'
                  }
                }"
              ></EditableValue>
            </div>
          </div>
        </div>

        <div class="w-full flex justify-start gap-12">
          <div class="flex gap-3">
            <div
              class="row-label-text"
              @click="$refs.featuredEditable.toggleCheckbox()"
            >
              Featured
            </div>

            <div class="flex items-center">
              <EditableValue
                ref="featuredEditable"
                :value="campaign.isFeatured"
                :mutation="UPDATE_CAMPAIGN_MUTATION"
                type="checkbox"
                :variables="{
                  input: {
                    id: campaign.id,
                    isFeatured: '{value}'
                  }
                }"
              ></EditableValue>
            </div>
          </div>

          <div class="flex gap-3">
            <div
              class="row-label-text"
              @click="$refs.isPinnedEditable.toggleCheckbox()"
            >
              Pinned Campaign
            </div>
            <div class="flex items-center">
              <EditableValue
                ref="isPinnedEditable"
                :value="campaign.isPinned"
                :mutation="UPDATE_CAMPAIGN_MUTATION"
                type="checkbox"
                :variables="{
                  input: {
                    id: campaign.id,
                    isPinned: '{value}'
                  }
                }"
              ></EditableValue>
            </div>
          </div>
        </div>
      </div>

      <div v-if="campaign.smartLink == 1" class="flex">
        <div
          class="row-label"
          @click="$refs.smartLinkEditable.toggleCheckbox()"
        >
          Prefered Algorithm
        </div>

        <div class="row-content">
          <EditableValue
            ref="prefferedAlgorithmEditable"
            :value="campaign.preferredAlgorithmId"
            type="slot"
            :mutation="UPDATE_CAMPAIGN_MUTATION"
            :variables="{
              input: {
                id: campaign.id,
                preferredAlgorithmId: '{value}'
              }
            }"
          >
            <template v-slot:default="slotProps">
              <div v-if="slotProps.value && slotProps.value.length > 0">
                <Tag
                  v-for="tag in slotProps.value"
                  :key="tag"
                  class="mr-1 my-1"
                  tabindex="0"
                  @focus="
                    () => {
                      slotProps.focus()
                    }
                  "
                  >{{ tag }}</Tag
                >
              </div>
              <div
                v-else
                @click="
                  () => {
                    slotProps.focus()
                  }
                "
              >
                <span
                  class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                  >None</span
                >
              </div>
            </template>
            <template v-slot:focusSlot="slotProps">
              <Superselect
                title="Algorithm Id"
                :value="slotProps.value"
                @input="
                  value => {
                    slotProps.updateInternalValue(value)
                  }
                "
                :focusOnMount="true"
                @close="() => {}"
                :multiple="false"
                placeholder="Select"
                class="box-width min-width-full w-24"
                :options="['2', '7']"
              ></Superselect>
            </template>
          </EditableValue>
        </div>
      </div>

      <div class="flex">
        <div class="row-label">Images</div>
        <div class="row-content flex flex-row gap-2">
          <ImageManager
            :availableTypes="
              campaign.showFrontend === 'All In Affiliates'
                ? ['logo', 'logo-dark']
                : undefined
            "
            :images="campaign.images"
            @submitted="
              image => {
                saveImage(image)
              }
            "
          />
        </div>
      </div>

      <div class="flex">
        <div class="row-label">Brand License</div>

        <div class="row-content">
          <EditableValue
            v-if="licenseOptions"
            ref="licenseEditable"
            title="License"
            :value="licenseValue"
            trackby="label"
            label="label"
            :mutation="UPDATE_CAMPAIGN_MUTATION"
            input-style="min-width: 14rem; max-width: 100%;"
            type="dropdown"
            :dropdownOptions="licenseOptions || []"
            :variables="{
              input: {
                id: campaign.id,
                license: '{value}'
              }
            }"
          ></EditableValue>
        </div>
      </div>

      <div class="flex">
        <div class="row-label" @click="$refs.verticalsEditable.focus()">
          Verticals
        </div>
        <div class="row-content">
          <EditableValue
            ref="verticalsEditable"
            :value="campaign.tags.filter(tag => tag.type === 'vertical')"
            type="slot"
            :mutation="SYNC_TAGS_WITH_TYPE_MUTATION"
            sortBy="id"
            :transformValueForQuery="
              value => {
                return value.map(tag => tag.name)
              }
            "
            :variables="{
              input: {
                type: 'vertical',
                taggableType: 'campaign',
                taggableId: campaign.id,
                tags: '{value}'
              }
            }"
          >
            <template v-slot:default="slotProps">
              <div v-if="slotProps.value && slotProps.value.length > 0">
                <Tag
                  v-for="tag in slotProps.value"
                  :key="tag.id"
                  class="mr-1 my-1"
                  :color="tag.color"
                  tabindex="0"
                  @focus="
                    () => {
                      slotProps.focus()
                    }
                  "
                  >{{ tag.name }}</Tag
                >
              </div>
              <div
                v-else
                @click="
                  () => {
                    slotProps.focus()
                  }
                "
              >
                <span
                  class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                  >None</span
                >
              </div>
            </template>
            <template v-slot:focusSlot="slotProps">
              <Superselect
                title="Verticals"
                :value="slotProps.value[0]"
                @input="
                  value => {
                    slotProps.updateInternalValue([value])
                  }
                "
                @close="
                  () => {
                    //slotProps.unfocusOnNoChanges() || slotProps.focusOnSave()
                  }
                "
                :focusOnMount="true"
                :multiple="false"
                track-by="slug"
                label="name"
                placeholder="Select"
                style="min-width: 10rem"
                :query="TAGS_QUERY"
                :query-result-function="
                  data => {
                    return filterVerticalTags(data)
                  }
                "
                :query-variables="{
                  filters: {
                    type: { value: ['vertical'] }
                  },
                  first: 100
                }"
              ></Superselect>
            </template>
          </EditableValue>
        </div>
      </div>

      <div>
        <CampaignTags :campaign="campaign" tag-type="trafficType" />
      </div>

      <div class="flex">
        <div class="row-label" @click="$refs.tagsEditable.focus()">
          Browser Type
        </div>
        <div class="row-content">
          <EditableValue
            ref="tagsEditable"
            :value="campaign.tags.filter(tag => tag.type === 'campaignBrowser')"
            type="slot"
            :mutation="SYNC_TAGS_WITH_TYPE_MUTATION"
            sortBy="id"
            :transformValueForQuery="
              value => {
                return value.map(tag => tag.name)
              }
            "
            trackBy="type"
            :variables="{
              input: {
                type: 'campaignBrowser',
                taggableType: 'campaign',
                taggableId: campaign.id,
                tags: '{value}'
              }
            }"
          >
            <template v-slot:default="slotProps">
              <div v-if="slotProps.value && slotProps.value.length > 0">
                <Tag
                  v-for="tag in slotProps.value"
                  :key="tag.id"
                  class="mr-1 my-1"
                  :color="tag.color"
                  tabindex="0"
                  @focus="
                    () => {
                      slotProps.focus()
                    }
                  "
                  >{{ tag.name }}</Tag
                >
              </div>
              <div
                v-else
                @click="
                  () => {
                    slotProps.focus()
                  }
                "
              >
                <span
                  class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                  >None</span
                >
              </div>
            </template>
            <template v-slot:focusSlot="slotProps">
              <Superselect
                title="Tags"
                :value="slotProps.value"
                @input="
                  value => {
                    slotProps.updateInternalValue(value)
                  }
                "
                @close="
                  () => {
                    //slotProps.unfocusOnNoChanges() || slotProps.focusOnSave()
                  }
                "
                :focusOnMount="true"
                :multiple="true"
                track-by="slug"
                label="name"
                placeholder="Select"
                class="box-width min-width-full"
                :query="TAGS_QUERY"
                :query-variables="{
                  filters: {
                    type: { value: ['campaignBrowser'] }
                  },
                  first: 100
                }"
              ></Superselect>
            </template>
          </EditableValue>
        </div>
      </div>

      <div class="flex">
        <div class="row-label" @click="$refs.tagsEditable.focus()">OS Type</div>
        <div class="row-content">
          <EditableValue
            ref="tagsEditable"
            :value="campaign.tags.filter(tag => tag.type === 'campaignOS')"
            type="slot"
            :mutation="SYNC_TAGS_WITH_TYPE_MUTATION"
            sortBy="id"
            :transformValueForQuery="
              value => {
                return value.map(tag => tag.name)
              }
            "
            trackBy="type"
            :variables="{
              input: {
                type: 'campaignOS',
                taggableType: 'campaign',
                taggableId: campaign.id,
                tags: '{value}'
              }
            }"
          >
            <template v-slot:default="slotProps">
              <div v-if="slotProps.value && slotProps.value.length > 0">
                <Tag
                  v-for="tag in slotProps.value"
                  :key="tag.id"
                  class="mr-1 my-1"
                  :color="tag.color"
                  tabindex="0"
                  @focus="
                    () => {
                      slotProps.focus()
                    }
                  "
                  >{{ tag.name }}</Tag
                >
              </div>
              <div
                v-else
                @click="
                  () => {
                    slotProps.focus()
                  }
                "
              >
                <span
                  class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                  >None</span
                >
              </div>
            </template>
            <template v-slot:focusSlot="slotProps">
              <Superselect
                title="Tags"
                :value="slotProps.value"
                @input="
                  value => {
                    slotProps.updateInternalValue(value)
                  }
                "
                @close="
                  () => {
                    //slotProps.unfocusOnNoChanges() || slotProps.focusOnSave()
                  }
                "
                :focusOnMount="true"
                :multiple="true"
                track-by="slug"
                label="name"
                placeholder="Select"
                class="box-width min-width-full"
                :query="TAGS_QUERY"
                :query-variables="{
                  filters: {
                    type: { value: ['campaignOS'] }
                  },
                  first: 100
                }"
              ></Superselect>
            </template>
          </EditableValue>
        </div>
      </div>

      <div class="flex">
        <div class="row-label" @click="$refs.tagsEditable.focus()">
          Campaign Tags
        </div>
        <div class="row-content">
          <EditableValue
            ref="tagsEditable"
            :value="campaign.tags.filter(tag => tag.type === 'campaign')"
            type="slot"
            :mutation="SYNC_TAGS_WITH_TYPE_MUTATION"
            sortBy="id"
            :transformValueForQuery="
              value => {
                return value.map(tag => tag.name)
              }
            "
            trackBy="type"
            :variables="{
              input: {
                type: 'campaign',
                taggableType: 'campaign',
                taggableId: campaign.id,
                tags: '{value}'
              }
            }"
          >
            <template v-slot:default="slotProps">
              <div v-if="slotProps.value && slotProps.value.length > 0">
                <Tag
                  v-for="tag in slotProps.value"
                  :key="tag.id"
                  class="mr-1 my-1"
                  :color="tag.color"
                  tabindex="0"
                  @focus="
                    () => {
                      slotProps.focus()
                    }
                  "
                  >{{ tag.name }}</Tag
                >
              </div>
              <div
                v-else
                @click="
                  () => {
                    slotProps.focus()
                  }
                "
              >
                <span
                  class="italic text-gray-500 hover:bg-blue-200 cursor-pointer"
                  >None</span
                >
              </div>
            </template>
            <template v-slot:focusSlot="slotProps">
              <Superselect
                title="Tags"
                :value="slotProps.value"
                @input="
                  value => {
                    slotProps.updateInternalValue(value)
                  }
                "
                @close="
                  () => {
                    //slotProps.unfocusOnNoChanges() || slotProps.focusOnSave()
                  }
                "
                :focusOnMount="true"
                :multiple="true"
                track-by="slug"
                label="name"
                placeholder="Select"
                class="box-width min-width-full"
                :query="TAGS_QUERY"
                :query-variables="{
                  filters: {
                    type: { value: ['campaign'] }
                  },
                  first: 100
                }"
              ></Superselect>
            </template>
          </EditableValue>
        </div>
      </div>

      <div class="flex">
        <div class="row-label">Actions</div>
        <div class="row-content">
          <ClickAndReplace :duration="2000">
            <template v-slot>
              <YButton
                class="bg-gray-100 hover:bg-gray-200 text-gray-700"
                :is-loading="isDeleting"
                >Delete Campaign</YButton
              >
            </template>
            <template v-slot:replacement>
              <YButton
                class="bg-red-600 hover:bg-red-700 text-white"
                @click="deleteCampaign"
                :is-loading="isDeleting"
                >Confirm Delete</YButton
              >
            </template>
          </ClickAndReplace>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CAMPAIGN_QUERY from '@/graphql/Campaign/CampaignQuery.gql'
import UPDATE_CAMPAIGN_MUTATION from '@/graphql/Campaign/UpdateCampaignMutation.gql'
import SYNC_TAGS_WITH_TYPE_MUTATION from '@/graphql/Tag/SyncTagsWithTypeMutation.gql'
import DELETE_CAMPAIGN_MUTATION from '@/graphql/Campaign/DeleteCampaignMutation.gql'
import EditModelPublisherPermissions from '@/views/Admin/Permissions/EditModelPublisherPermissions'
import SubscribeToCampaignWidget from '@/views/Admin/Campaign/SubscribeToCampaignWidget'
import ClickAndReplace from '@/components/ui/ClickAndReplace'
import IMAGES_QUERY from '@/graphql/ImageRepository/ImagesQuery.gql'
import ImageManager from '@/components/general/ImageManager'
import ME_QUERY from '@/graphql/User/MeQuery.gql'
import CampaignTags from '@/views/Admin/Campaign/CampaignTags.vue'

export default {
  components: {
    ClickAndReplace,
    ImageManager,
    CampaignTags
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  apollo: {
    me: {
      query: ME_QUERY,
      result({ data }) {
        if (data && data.me) {
          this.me = data.me
        }
      }
    },
    campaign: {
      query: CAMPAIGN_QUERY,
      variables() {
        return {
          id: this.id
        }
      }
    }
  },
  data() {
    return {
      UPDATE_CAMPAIGN_MUTATION,
      SYNC_TAGS_WITH_TYPE_MUTATION,
      IMAGES_QUERY,
      isDeleting: false,
      isUploading: false,
      campaign: null,
      imageFile: null,
      baselineGeo: 'Default',
      baselineValue: null,
      baselineMinimum: null,
      baselineDeposits: [],
      baselineCountries: [],
      licenseOptions: [
        { countryCode: 'KM', label: 'Anjouan Gaming License' },
        { countryCode: 'BR', label: 'Brazil License' },
        { countryCode: 'CR', label: 'Costa Rica License' },
        { countryCode: 'CW', label: 'Curaçao License' },
        { countryCode: 'EE', label: 'Estonia License' },
        { countryCode: 'GE', label: 'Georgia License' },
        { countryCode: 'MT', label: 'Malta License' },
        { countryCode: 'ON-CA', label: 'Ontario License' },
        { countryCode: 'PA', label: 'Panama License' },
        { countryCode: 'PH', label: 'Philippines License' },
        { countryCode: 'GB', label: 'UKGC License' }
      ],
      allInVerticalTags: ['Casino', 'Sports Betting']
    }
  },
  created() {
    if (this.campaign && this.imageFileComputed?.length > 0) {
      this.imageFile = this.imageFileComputed[0]
    }

    this.baselineDeposits = JSON.parse(this.campaign.baselineDeposits) ?? []

    this.campaign.payouts.forEach(payout => {
      this.baselineCountries.push(payout.countryCode)
    })
    this.baselineCountries = [...new Set(this.baselineCountries), 'Default']
  },
  computed: {
    isSalesAdmin() {
      return (
        this.me &&
        this.me.roles &&
        this.me.roles.some(role => role.name === 'sales-admin-level-1')
      )
    },
    licenseValue() {
      if (this.campaign.license) {
        return JSON.parse(this.campaign.license)
      }
      return null
    },
    imageHasUnsavedChanges() {
      const campaignId = this.campaign?.images?.[0]?.id ?? null
      const imageFileId = this.imageFile?.id ?? null

      return campaignId !== imageFileId
    },
    advertisers() {
      if (this.campaign) {
        return this.campaign.advertisers
      } else {
        return null
      }
    },
    imageFileComputed() {
      if (this.campaign.images) {
        return this.campaign.images
          .filter(image => image.type === 'template')
          .map(image => {
            return {
              id: image.id,
              name: image.name,
              imageThumbnailUrl: image.conversions.find(
                image => image.conversion === 'thumb'
              ).storageUrl
            }
          })
      }
      return null
    }
  },
  methods: {
    filterVerticalTags(data) {
      let tags = data.tags.data || []

      if (this.campaign.showFrontend == 'yTrack') {
        return tags.filter(tag => !this.allInVerticalTags.includes(tag.name))
      } else {
        return tags.filter(tag => this.allInVerticalTags.includes(tag.name))
      }
    },
    updateBaselineValues() {
      this.$apollo
        .mutate({
          mutation: UPDATE_CAMPAIGN_MUTATION,
          variables: {
            input: {
              id: this.campaign.id,
              baselineDeposits: this.baselineDeposits
            }
          }
        })
        .then(() => {
          this.isUploading = false
          this.$toastr.s('Baseline Deposits Updated')
        })
        .catch(error => {
          this.isUploading = false
          console.error(error)
        })
    },
    deleteRow(props) {
      this.baselineDeposits = this.baselineDeposits.filter(
        item => item !== props
      )
      this.updateBaselineValues()
    },
    addNewBaselineRow() {
      if (!this.baselineGeo || !this.baselineValue || !this.baselineMinimum) {
        return
      }

      this.baselineDeposits.push({
        geo: this.baselineGeo,
        baseline: this.baselineValue,
        minimum: this.baselineMinimum
      })

      this.baselineGeo = null
      this.baselineValue = null
      this.baselineMinimum = null
      this.updateBaselineValues()
    },
    cancelImage() {
      if (this.campaign && this.imageFileComputed?.length > 0) {
        this.imageFile = this.imageFileComputed[0]
      }
      return (this.imageFile = null)
    },
    deleteCampaign() {
      this.isDeleting = true
      this.$apollo
        .mutate({
          mutation: DELETE_CAMPAIGN_MUTATION,
          variables: {
            id: this.campaign.id
          }
        })
        .then(() => {
          this.isDeleting = false
          this.$toastr.s('Campaign Deleted')
          this.$router.push({ name: 'Campaigns' })
        })
        .catch(error => {
          this.isDeleting = false
          console.error(error)
        })
    },
    saveImage({ image, type }) {
      this.$apollo
        .mutate({
          mutation: UPDATE_CAMPAIGN_MUTATION,
          variables: {
            input: {
              id: this.campaign.id,
              images: [{ id: image.id, type: type }]
            }
          }
        })
        .then(() => {
          this.isUploading = false
          this.$toastr.s('Image Saved')
        })
        .catch(error => {
          this.isUploading = false
          console.error(error)
        })
    },
    openSubscribeUserModal() {
      this.$modal.show(
        SubscribeToCampaignWidget,
        {
          campaignId: this.id
        },
        {
          classes: ['overflow-visible-important'],
          height: 'auto',
          scrollable: true,
          width: '80%',
          maxWidth: 600,
          minWidth: 200,
          adaptive: true
        }
      )
    },
    openPermissionsModal() {
      this.$modal.show(
        EditModelPublisherPermissions,
        {
          modelIds: [this.id],
          modelClass: 'campaign',
          ability: 'query',
          all: this.campaign.publisherAbilitySummary.all,
          only: this.campaign.publisherAbilitySummary.only,
          except: this.campaign.publisherAbilitySummary.except,
          onSuccess: () => {}
        },
        {
          classes: ['overflow-visible-important'],
          height: 'auto',
          scrollable: true,
          width: '80%',
          maxWidth: 600,
          minWidth: 200,
          adaptive: true
        }
      )
    }
  }
}
</script>

<style scoped>
.row-label-text {
  @apply font-bold text-gray-700;
}
.row-label {
  @apply w-48 text-left px-4 font-bold text-gray-700;
  min-width: 12rem;
}
.row-content {
  @apply w-auto;
}
/*  */
.x-row {
  @apply flex items-center py-4;
}
.x-col1 {
  @apply w-48 text-right px-4 font-bold text-gray-700;
  min-width: 12rem;
}
.x-col2 {
  @apply flex-grow;
}
.box-width {
  max-width: 20rem;
}
</style>
